import {
  TextInput as RawTextInput,
  TextInputProps as RawTextInputProps,
} from '@mantine/core';
import { toSentenceCase, toTitleCase } from 'common/utils/string';
import { FontCase, FontCaseType } from 'components/elements/fields/text-field';
import { useFormState } from 'components/elements/form';
import React from 'react';
import { useController } from 'react-hook-form';

export interface TextInputProps extends Omit<RawTextInputProps, 'type'> {
  type: 'text';
  name: string;
  fontCase?: FontCaseType;
}

export default function TextInput(props: TextInputProps) {
  const {
    disabled,
    readOnly,
    name,
    type,
    error,
    placeholder,
    fontCase = FontCase.Default,
    ...rest
  } = props;
  const formState = useFormState();
  const { field, fieldState } = useController({
    name,
  });

  const _disabled = formState.editable === false || disabled || readOnly;
  const _error = fieldState.error?.message || error;

  const _placeholder = React.useMemo(() => {
    if (_disabled) return undefined;
    return placeholder;
  }, [_disabled, placeholder]);

  const withAsterisk = React.useMemo(() => {
    if (_disabled) return false;
    if (rest.required || rest.withAsterisk) return true;
  }, [_disabled, rest.required, rest.withAsterisk]);

  const textTransformer = React.useCallback(
    (text: string) => {
      switch (fontCase) {
        case 'TitleCase':
          return toTitleCase(text);
        case 'SentenceCase':
          return toSentenceCase(text);
        case 'Default':
        default:
          return text;
      }
    },
    [fontCase],
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const result = textTransformer(e.target.value);
      field.onChange(result);
      props.onChange?.(e);
    },
    [field, props, textTransformer],
  );

  return (
    <RawTextInput
      {...rest}
      {...field}
      type={type}
      placeholder={_placeholder}
      value={field.value}
      onChange={onChange}
      disabled={_disabled}
      error={_error}
      withAsterisk={withAsterisk}
    />
  );
}
